import React, { FC } from 'react'
import classNames from 'classnames'
import { AsSpan, Text } from '@/atoms/Text'

interface Props {
  title: string
  discountLabel: string
  selected: boolean
  onClick: () => void
  ticketQuantity: number
  ticketPrice: number
  discountRate: number
  specialTag?: string | null
  isHigherDiscount?: boolean
}

export const TicketOptionDiscount: FC<Props> = ({
  title,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  discountLabel,
  selected,
  onClick,
  ticketPrice,
  ticketQuantity,
  discountRate,
  specialTag,
  isHigherDiscount = false,
}) => {
  const originalPrice = ticketQuantity * ticketPrice
  const discountedPrice = ticketQuantity * ticketPrice * discountRate
  // Calculate discount percentage (100% - discountRate*100%)
  const discountPercentage = Math.round((1 - discountRate) * 100)
  // Format price without decimal zeros
  const formatPrice = (price: number): string => {
    return price.toFixed(2).replace(/\.00$/, '')
  }

  // Get background color for discount badge
  const getDiscountBadgeStyle = () => {
    if (selected) {
      return isHigherDiscount
        ? 'bg-error-400/20 text-error-400 border border-error-400/40'
        : 'bg-core-warning-300/20 text-core-warning-300 border border-core-warning-300/40'
    }
    return isHigherDiscount
      ? 'bg-warm-01/15 text-warm-01 border border-warm-01/30'
      : 'bg-warm-03/15 text-warm-03 border border-warm-03/30'
  }

  return (
    <div key={title} className="radio grow-1 select-none" onClick={onClick}>
      <label className="flex w-full cursor-pointer items-center justify-center gap-2">
        <span
          className={classNames(
            'flex gap-2 items-center py-3 pl-4 pr-6 border rounded-full flex-grow-1 max-w-[380px] w-full relative',
            selected ? 'border-core-gray-950 bg-core-gray-950' : 'border-core-gray-400 hover:bg-core-gray-100',
          )}
        >
          {specialTag && (
            <div
              className="absolute -top-3 right-12 z-10"
              style={{
                backgroundColor: '#DD2E44',
                padding: '0.25rem 0.75rem',
                borderRadius: '9999px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                fontSize: '0.75rem',
                fontWeight: 'bold',
                color: 'white',
              }}
            >
              {specialTag}
            </div>
          )}
          <span
            className={classNames(
              'relative h-6 w-6 rounded-full border',
              selected ? 'bg-oxide-bright border-oxide-bright' : 'border-core-gray-400',
            )}
          >
            {selected && (
              <span className="absolute inset-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-black" />
            )}
          </span>
          <span className="flex flex-col gap-1 text-left">
            <div className="flex items-center gap-2">
              <Text
                as={AsSpan}
                weight="bold"
                className={classNames('block text-lg leading-5', selected ? 'text-white' : 'text-core-gray-950')}
              >
                {title}
              </Text>
              <span
                className={classNames(
                  'inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold',
                  getDiscountBadgeStyle(),
                )}
              >
                {discountPercentage}% OFF
              </span>
            </div>
          </span>
          <span className="ml-auto flex flex-col gap-1 text-right">
            <Text
              as={AsSpan}
              weight="medium"
              color="core-gray-600"
              className={classNames(
                'block line-through italic leading-4 text-sm',
                selected ? '!text-core-gray-500' : '!text-core-gray-600',
              )}
            >
              ${formatPrice(originalPrice)}
            </Text>
            <Text
              as={AsSpan}
              weight="bold"
              className={classNames('text-base leading-5', selected ? '!text-white' : '!text-core-gray-950')}
            >
              ${formatPrice(discountedPrice)}
            </Text>
          </span>
        </span>
      </label>
    </div>
  )
}
